/*
 * This file is part of glowingblue/orell-bookshelf.
 *
 * Copyright (c) 2023 Glowing Blue AG.
 * Authors: Rafael Horvat.
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import app from 'flarum/forum/app';
import LogInModal from 'flarum/forum/components/LogInModal';
import type Model from 'flarum/common/Model';
import type { ApiQueryParamsSingle, ApiResponseSingle } from 'flarum/common/Store';
import BookListState from './BookListState';
import type Bookshelf from '../../common/models/Bookshelf';

export interface BookshelfPageParams extends ApiQueryParamsSingle {
	filter?: { status: string };
	sort?: string;
}

/** `m.route.param()`'s no-argument overload is typed `any`; this shape keeps call sites typed. */
export interface BookshelfRouteParams {
	sort?: string;
	filter?: { status?: string };
}

export default class BookshelfPageState {
	loading: boolean = true;

	bookshelf: ApiResponseSingle<Bookshelf> | null = null;

	get params(): BookshelfPageParams {
		const params: BookshelfPageParams = {};
		const routeParams: BookshelfRouteParams | undefined = m.route.param();

		const status = routeParams?.filter?.status;
		if (status) {
			params.filter = { status };
		}

		const sort = routeParams?.sort;
		if (sort) {
			params.sort = BookListState.prototype.sortMap()[sort];
		}

		return params;
	}

	get bookshelfId(): number {
		return Number.parseInt(m.route.param('id'));
	}

	async refresh(): Promise<void> {
		const id = this.bookshelfId;
		if (this.bookshelf && id !== (this.bookshelf.id() as unknown)) {
			this.bookshelf = null;
		}

		// Evict the cached record: a response without `relationships.articles` would otherwise leave
		// the previous relationships in place. `Store.data` is `protected` with no public equivalent.
		try {
			const store = app.store as unknown as { data: Record<string, Record<string, Model>> };
			delete store.data['bookshelves'][id];
		} catch (error) {}

		this.loading = true;

		if (!app.session.user && !app.forum.attribute('canViewBookshelves')) {
			app.modal.show(LogInModal);
			return;
		}
		m.redraw();

		let bookshelf: ApiResponseSingle<Bookshelf>;

		const preloadedBookshelf = app.preloadedApiDocument<Bookshelf>();
		if (preloadedBookshelf) {
			bookshelf = preloadedBookshelf;
		} else {
			bookshelf = await app.store.find<Bookshelf>('bookshelves', m.route.param('id'), this.params);
		}

		this.show(bookshelf);
	}

	show(bookshelf: ApiResponseSingle<Bookshelf>): void {
		this.bookshelf = bookshelf;
		this.loading = false;
		m.redraw();
	}
}
